/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject, JSX } from 'preact';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { BundleType } from '#resources/nls/bundle';
import { classNames } from '#utils/UNSAFE_classNames';
import { DateReducerAction } from './dateReducer';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { EditableDateSegment } from './types';
import { isBackspaceOrDelete, isSelectAll } from '#utils/PRIVATE_keyboardUtils';
import { isNumberOnlyString } from '#utils/PRIVATE_dayUtils';
import { segmentStyles } from './themes/SegmentStyles.css';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { useUser } from '#hooks/UNSAFE_useUser';

type BaseProps = Pick<
  UnpackSignals<JSX.AriaAttributes>,
  'aria-label' | 'aria-valuemax' | 'aria-valuemin' | 'aria-valuenow' | 'aria-valuetext'
>;
type AriaProps = Required<Pick<BaseProps, 'aria-valuemin' | 'aria-valuemax'>> & // these two required
  Omit<BaseProps, 'aria-valuemin' | 'aria-valuemax'>; // the rest as in BaseProps

type Props = AriaProps & {
  /** A ref that is provided for the first segment in a date field. */
  inputRef?: RefObject<HTMLDivElement>;

  /**
   * True if the segment is complete in the sense that it contains a complete valid value.
   * For instance, entering '0' and then '1-9' is considered a complete value for month and day.
   * Also true if typing any additional digit would cause the segment value to exceed the max.
   */
  isComplete?: boolean;

  /**
   * True if the segment is disabled.
   */
  isDisabled?: boolean;

  /**
   * True if the field is in focused state.
   */
  isFieldFocused?: boolean;

  /**
   * True if the segment should be hidden.
   */
  isHidden?: boolean;

  /**
   * True if the segment is highlighted.
   */
  isHighlighted?: boolean;

  /**
   * True if the segment is invalid.
   */
  isInvalid?: boolean;

  /**
   * Specifies for accessibility purposes whether a value is required.
   */
  isRequired?: boolean;

  /**
   * True if the segment is selected.
   */
  isSelected?: boolean;

  /**
   * The order of the segments, like ['month', 'day', 'year']. This is necessary to pass into the dispatch actions
   */
  orderedSegments: EditableDateSegment[];

  /**
   * Placeholder displayed when the segment has no display value.
   */
  placeholder?: string;

  /**
   * The text to display for this segment.
   */
  text?: string;

  /**
   * The segment of a calendar date which is being represented, i.e. the month, day, or year.
   */
  type: EditableDateSegment;

  /**
   * Callback invoked when a change to the segment is requested via user action, such as typing
   * a new value or pressing the Backspace or Delete keys.
   */
  onChange?: (action: DateReducerAction) => void;
};

// Used to disable browser cut and paste, which don't really work with segments (copy is fine).
const preventDefaultHandler = (e: Event) => {
  e.preventDefault();
};

const isIos = () => getClientHints().platform === 'ios';

/**
 * A DateSegment renders a part of a calendar date, e.g. the day, month, or year.
 * It can be used as a spinbutton to step or spin its value.
 */
export const DateSegment = ({
  'aria-label': ariaLabel,
  'aria-valuemax': ariaValueMax,
  'aria-valuemin': ariaValueMin,
  'aria-valuenow': ariaValueNow,
  'aria-valuetext': ariaValueText,
  inputRef,
  isComplete,
  isDisabled,
  isFieldFocused,
  isHidden,
  isHighlighted,
  isInvalid,
  isRequired,
  isSelected,
  onChange,
  orderedSegments,
  placeholder,
  text,
  type
}: Props) => {
  const hasDisplayValue = text && text.length > 0;
  const rootClasses = classNames([
    segmentStyles.base,
    isSelected && segmentStyles.selected,
    isHighlighted && segmentStyles.highlighted,
    isFieldFocused && segmentStyles.accessibleTouchTarget
  ]);
  const placeholderClasses = classNames([
    segmentStyles.placeholder,
    isHidden || hasDisplayValue ? segmentStyles.hidden : segmentStyles.notHidden
  ]);

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const emptySegmentValueText = translations.inputDateMask_empty_segment();

  // If a ref was passed in, use it, otherwise get a ref.
  const sRef = useRef<HTMLDivElement>(null);
  const segmentRef = inputRef ?? sRef;

  const hasLeadingZero = useRef<boolean>(false);
  const { locale } = useUser();

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        isDisabled ||
        event.key === 'Tab' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight' ||
        event.key === 'Enter' ||
        // JET-73224: let Escape pass through in order to close a popup or dialog.
        event.key === 'Escape' ||
        isSelectAll(event)
      ) {
        // Let the event pass through.
        return;
      }

      if (isHighlighted) {
        // If date is selected, ignore edits and let backspace/delete pass through to clear.
        if (isBackspaceOrDelete(event) || event.key === 'Backspace') {
          return;
        }
      } else if (event.key === '0' && (isComplete || text === undefined)) {
        // Typing '0' is considered a leading zero if the segment is complete, in which case
        // the user is typing a new value, or if there is no current value.
        hasLeadingZero.current = true;
      } else if (isNumberOnlyString(event.key)) {
        onChange?.({
          actionType: 'updateSegment',
          data: {
            type,
            userInput: event.key,
            hasLeadingZero: hasLeadingZero.current,
            locale,
            orderedSegments,
            existingText: text,
            min: ariaValueMin,
            max: ariaValueMax
          }
        });
        hasLeadingZero.current = false;
      } else if (isBackspaceOrDelete(event) || event.key === 'Backspace') {
        // Android sends Backspace as event.key, not event.code, so check for it.
        onChange?.({ actionType: 'clearSegment', data: { type, orderedSegments } });
        hasLeadingZero.current = false;
      } else if (event.key === 'ArrowUp') {
        onChange?.({
          actionType: 'step',
          data: {
            direction: 'increase',
            locale,
            type,
            orderedSegments,
            min: ariaValueMin,
            max: ariaValueMax
          }
        });
        hasLeadingZero.current = false;
      } else if (event.key === 'ArrowDown') {
        onChange?.({
          actionType: 'step',
          data: {
            direction: 'decrease',
            locale,
            type,
            orderedSegments,
            min: ariaValueMin,
            max: ariaValueMax
          }
        });
        hasLeadingZero.current = false;
      } else if (event.key === 'PageUp') {
        onChange?.({
          actionType: 'page',
          data: {
            direction: 'increase',
            locale,
            type,
            orderedSegments,
            min: ariaValueMin,
            max: ariaValueMax
          }
        });
        hasLeadingZero.current = false;
      } else if (event.key === 'PageDown') {
        onChange?.({
          actionType: 'page',
          data: {
            direction: 'decrease',
            locale,
            type,
            orderedSegments,
            min: ariaValueMin,
            max: ariaValueMax
          }
        });
        hasLeadingZero.current = false;
      } else if (event.key === 'Home') {
        onChange?.({
          actionType: 'goToHome',
          data: { locale, type, orderedSegments, min: ariaValueMin }
        });
        hasLeadingZero.current = false;
      } else if (event.key === 'End') {
        onChange?.({
          actionType: 'goToEnd',
          data: { locale, type, orderedSegments, max: ariaValueMax }
        });
        hasLeadingZero.current = false;
      }
      event.preventDefault();
      event.stopPropagation();
    },
    [
      ariaValueMax,
      ariaValueMin,
      isComplete,
      isDisabled,
      isHighlighted,
      locale,
      onChange,
      orderedSegments,
      text,
      type
    ]
  );

  // Used to focus the segment when isSelected is true.
  useEffect(() => {
    if (isSelected) {
      segmentRef.current?.focus();
    }
  }, [isSelected, segmentRef]);

  // This attribute is used to distinguish between segments when clicked (see DateField.tsx).
  const customDataAttribute = { 'data-segment': [type] };

  // If ariaValueText is undefined, substitute a string that indicates the segment is empty.
  // If ariaValueNow and ariaValueText represent the same value, omit the value text; this
  // is the same as what we do in NumberInputText.
  const valueText =
    ariaValueText === undefined
      ? emptySegmentValueText
      : ariaValueNow?.toString() === ariaValueText
      ? undefined
      : ariaValueText;

  return (
    // Introduce a div wrapper around the contentEditable element to prevent it from grabbing focus.
    // https://stackoverflow.com/questions/34354085/clicking-outside-a-contenteditable-div-stills-give-focus-to-it
    // JET-64874: We change the segment's role to "textbox" on iOS to work around a VoiceOver bug, and when we do that
    // we need to add aria-multiline={false}
    <div>
      <div
        ref={segmentRef}
        role={isIos() ? 'textbox' : 'spinbutton'}
        aria-disabled={isDisabled}
        aria-invalid={isInvalid || undefined}
        aria-label={ariaLabel}
        aria-multiline={isIos() ? false : undefined}
        aria-required={isRequired}
        aria-valuemin={isIos() ? undefined : ariaValueMin}
        aria-valuemax={isIos() ? undefined : ariaValueMax}
        aria-valuenow={isIos() ? undefined : ariaValueNow}
        aria-valuetext={isIos() ? undefined : valueText}
        autocapitalize="off"
        autocorrect="off"
        class={rootClasses}
        contentEditable={!isDisabled}
        {...customDataAttribute}
        enterkeyhint={isDisabled ? undefined : 'done'}
        inputMode={isDisabled ? 'none' : 'numeric'}
        onCut={preventDefaultHandler}
        onKeyDown={onKeyDown}
        onPaste={preventDefaultHandler}
        spellcheck={false}
        tabIndex={isSelected ? 0 : -1}>
        <span aria-hidden="true" class={placeholderClasses} {...customDataAttribute}>
          {placeholder}
        </span>
        {text}
      </div>
    </div>
  );
};
